import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyView } from '@api-proxies';
import { ContactService } from '@http/clients/contact.service';
import { Contact } from '@models/Contact';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ModalService, ObjectExtensionsService, SnackbarNotificationService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { ContactsSingleOverviewComponent } from '../contacts-single/contacts-single-overview/contacts-single-overview.component';

@Component({
    selector: 'app-contacts-edit',
    templateUrl: './contacts-edit.component.html',
    styleUrls: ['./contacts-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SectionHeaderComponent, ContactsSingleOverviewComponent]
})
export class ContactsEditComponent implements OnInit, OnDestroy {
    @ViewChild('overview')
    overview?: ContactsSingleOverviewComponent;

    contact: Contact;

    contactId: number;

    buttonLabel?: string;

    continueButtonLabel?: string;

    title?: string;

    selectedCompany?: CompanyView;

    contactInitialState!: Contact;

    entitySubscription?: Subscription;

    constructor(
        private readonly awaiter: AwaiterService,
        private readonly contactService: ContactService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly modalService: ModalService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly objectExtensionService: ObjectExtensionsService
    ) {
        this.contact = new Contact();
        this.contactId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.buttonLabel = 'Create';
        this.continueButtonLabel = 'Continue Creation';
        this.title = 'Create New Contact';
    }

    async ngOnInit(): Promise<void> {
        if (this.contactId) {
            this.buttonLabel = 'Save';
            this.continueButtonLabel = 'Continue Edition';
            this.title = 'Edit Contact';

            this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
                this.contact = entityData;
            });

            this.contactInitialState = this.objectExtensionService.clone(this.contact) as Contact;
        }
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    async checkSavingData(): Promise<void> {
        if (!this.overview?.validate()) return;

        if (this.contactId && this.contactInitialState.companyId !== this.contact.companyId) {
            const responseOk = await this.modalService.open(ModalConfirmComponent, {
                acceptCaption: 'Continue saving',
                cancelCaption: 'Cancel',
                content:
                    'Updates in Company will not be reflected in the Opportunities and Buildings previously linked',
                title: 'Company Update'
            });

            if (!responseOk) return;
        }

        await this.saveContact();
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: this.continueButtonLabel,
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Cancel Save Contact'
        });

        if (responseOk)
            this.router.navigate(
                this.contactId ? [`/contacts-single/${this.contactId}/${TabNames[TabNames.Overview]}`] : ['/contacts']
            );
    }

    private async saveContact(): Promise<void> {
        await this.awaiter.awaitAction('Saving Contact', async () => {
            await this.contactService.save(this.contact);
            this.snackbarNotificationService.success('Contact saved successfully.');
            this.router.navigate(
                this.contactId ? [`/contacts-single/${this.contactId}/${TabNames[TabNames.Overview]}`] : ['/contacts']
            );
        });
    }
}
